import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmarks as BookmarksIcon,
} from "@mui/icons-material";
import { Badge } from "@mui/material";
import { MenuButton, MenuItemWithIcon } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { PimProductPreferenceScope } from "../../../../clients/graphqlTypes.js";
import {
  isPartialSuccess,
  summariseAddProductPreferenceResults,
} from "../productPreferences.js";
import { useProductPreferences } from "../useProductPreferences.js";

interface Product {
  articleNumber: string;
  supplierId: string;
  /**
   * The article's own PIM classification, where the row carries it. A classification naming
   * a different product type than the one the hit's preferences are attributed to is marked
   * on the control.
   */
  productType?: {
    id: string;
    key: string;
    title: string;
    productTypeSystemKey: string;
  } | null;
  /** The hit's preference attribution, delivered in the same response as `productType`. */
  preferences?: readonly {
    productType: { productTypeKey: string; productTypeSystemKey: string };
  }[];
}

interface Props {
  /**
   * The one product type the preference is keyed to. The control is only rendered where a
   * single product type is unambiguously in context — see `getProductTypeInContext`.
   */
  productTypeId: string;
  product: Product;
  preferences: ReturnType<typeof useProductPreferences>;
}

/**
 * Marks a single article as preferred for one product type, at a scope the user picks
 * explicitly. Unlike the organisation favourites star this is never a single unqualified
 * click: `ORGANISATION` scope changes what colleagues see, so the scope is always chosen by
 * name.
 */
export function ProductPreferenceButton({
  productTypeId,
  product,
  preferences,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);

  const {
    preferredStateKnown,
    error,
    getPreferredScopes,
    addProductPreferences,
    deleteProductPreferences,
    writing,
  } = preferences;

  const scopes = getPreferredScopes(product);

  const articleNumberIdentifiers = React.useMemo(
    () => [
      { supplierId: product.supplierId, articleNumber: product.articleNumber },
    ],
    [product.supplierId, product.articleNumber]
  );

  const add = async (scope: PimProductPreferenceScope) => {
    let result;
    try {
      result = await addProductPreferences({
        variables: {
          input: {
            scope,
            productType: { productTypeId },
            articleNumberIdentifiers,
          },
        },
      });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
      return;
    }

    const results = result.data?.pimAddProductPreferences.results ?? [];
    const summary = summariseAddProductPreferenceResults(results);

    for (const conflict of summary.conflicts) {
      enqueueSnackbar(
        t(
          "This article is already classified as {productType}, so it cannot be preferred here.",
          {
            ns: "Product",
            productType: conflict.productTypeName,
          }
        ),
        { variant: "warning" }
      );
    }
    for (const unknown of summary.unknownSuppliers) {
      enqueueSnackbar(
        t(
          "Article {articleNumber} could not be preferred: its supplier is unknown.",
          {
            ns: "Product",
            articleNumber: unknown.articleNumber,
          }
        ),
        { variant: "error" }
      );
    }
    if (summary.added.length > 0) {
      enqueueSnackbar(
        isPartialSuccess(summary)
          ? t("Some articles were marked as preferred.", { ns: "Product" })
          : t("Marked as preferred.", {
              ns: "Product",
            }),
        { variant: "success" }
      );
    }
  };

  const remove = async (scope: PimProductPreferenceScope) => {
    try {
      await deleteProductPreferences({
        variables: {
          input: {
            scope,
            productType: { productTypeId },
            articleNumberIdentifiers,
          },
        },
      });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
      return;
    }
    enqueueSnackbar(t("Preference removed.", { ns: "Product" }), {
      variant: "success",
    });
  };

  // The article's classification differs from the product type its preference is attributed
  // to. Both sides are read from the hit itself, so a row kept on screen during a refetch
  // stays consistent with its own data.
  const divergingProductType =
    product.productType &&
    product.preferences?.some(
      preference =>
        preference.productType.productTypeKey !== product.productType?.key ||
        preference.productType.productTypeSystemKey !==
          product.productType?.productTypeSystemKey
    )
      ? product.productType
      : null;

  // A settled failure reads differently from an in-flight query: the control stays disabled
  // either way, so the tooltip is the only place the failure can surface.
  const preferredStateLabel = !preferredStateKnown
    ? (error?.message ?? t("Loading preferences", { ns: "Product" }))
    : scopes.user && scopes.organisation
      ? t("Preferred by you and your organisation", { ns: "Product" })
      : scopes.user
        ? t("Preferred by you", { ns: "Product" })
        : scopes.organisation
          ? t("Preferred by your organisation", {
              ns: "Product",
            })
          : t("Not preferred", { ns: "Product" });

  const label = divergingProductType
    ? `${preferredStateLabel} — ${t("Classified as {productType}", {
        ns: "Product",
        productType: divergingProductType.title,
      })}`
    : preferredStateLabel;

  const icon = !preferredStateKnown ? (
    // Unknown, not unpreferred — the muted outline avoids claiming a state the query has
    // not answered yet.
    <BookmarkBorderIcon fontSize="small" color="disabled" />
  ) : scopes.organisation ? (
    <BookmarksIcon fontSize="small" />
  ) : scopes.user ? (
    <BookmarkIcon fontSize="small" />
  ) : (
    <BookmarkBorderIcon fontSize="small" />
  );

  return (
    <MenuButton
      Icon={
        <Badge
          variant="dot"
          color="warning"
          overlap="circular"
          invisible={divergingProductType === null}
        >
          {icon}
        </Badge>
      }
      tooltip={label}
      buttonProps={{
        size: "small",
        color: "primary",
        disabled: !preferredStateKnown || writing,
        "aria-label": label,
      }}
    >
      {!scopes.user ? (
        <MenuItemWithIcon icon={<BookmarkIcon />} onClick={() => add("USER")}>
          {t("Prefer for me", { ns: "Product" })}
        </MenuItemWithIcon>
      ) : (
        <MenuItemWithIcon
          icon={<BookmarkBorderIcon />}
          onClick={() => remove("USER")}
        >
          {t("Remove my preference", {
            ns: "Product",
          })}
        </MenuItemWithIcon>
      )}
      {!scopes.organisation ? (
        <MenuItemWithIcon
          icon={<BookmarksIcon />}
          onClick={() => add("ORGANISATION")}
        >
          {t("Prefer for my whole organisation", {
            ns: "Product",
          })}
        </MenuItemWithIcon>
      ) : (
        <MenuItemWithIcon
          icon={<BookmarkBorderIcon />}
          onClick={() => remove("ORGANISATION")}
        >
          {t("Remove my organisation's preference", { ns: "Product" })}
        </MenuItemWithIcon>
      )}
    </MenuButton>
  );
}
